import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Unlock, Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function UnlockAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const userType = searchParams.get('type') || 'hospital';
  
  const [step, setStep] = useState(token ? 'verify' : 'request');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyUnlockToken();
    }
  }, [token]);

  const verifyUnlockToken = async () => {
    setLoading(true);
    try {
      await api.post(`/account/unlock/verify?token=${token}`);
      setSuccess(true);
      toast.success('Account unlocked successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired unlock link');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestUnlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await api.post('/account/unlock/request', {
        email,
        user_type: userType
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send unlock email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="bg-white/95 backdrop-blur shadow-2xl">
          <CardHeader className="text-center">
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              success ? 'bg-green-100' : 'bg-amber-100'
            }`}>
              {success ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <ShieldAlert className="h-8 w-8 text-amber-600" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {success 
                ? (step === 'verify' ? 'Account Unlocked!' : 'Check Your Email')
                : 'Account Locked'}
            </CardTitle>
            <CardDescription>
              {success 
                ? (step === 'verify' 
                    ? 'Your account has been unlocked. You can now login.'
                    : 'If your account is locked, you will receive an unlock link.')
                : 'Your account has been temporarily locked due to multiple failed login attempts'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {loading && step === 'verify' ? (
              <div className="text-center py-6">
                <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
                <p className="text-slate-600">Verifying unlock token...</p>
              </div>
            ) : success ? (
              <div className="text-center py-4">
                <Button
                  onClick={() => navigate(userType === 'platform' ? '/platform-admin' : '/login')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go to Login
                </Button>
              </div>
            ) : step === 'verify' && error ? (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Invalid Link</span>
                  </div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setStep('request');
                    setError('');
                  }}
                >
                  Request New Unlock Link
                </Button>
              </div>
            ) : (
              <form onSubmit={handleRequestUnlock} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    Enter your email address below to receive an unlock link. 
                    The link will be valid for 24 hours.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Unlock className="h-4 w-4 mr-2" />
                  )}
                  Send Unlock Link
                </Button>
                
                <div className="text-center">
                  <Link 
                    to={userType === 'platform' ? '/platform-admin' : '/login'}
                    className="text-sm text-purple-600 hover:underline inline-flex items-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
